"use client";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { cn } from "@/lib/utils";
import { ExportIcon } from "@phosphor-icons/react";
import { useCallback } from "react";

const CLASSROOM_ICON_SRC = "/images/icons/googleClassroom-icon.svg";

function canUseNativeShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

interface GameShareSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classroomHref: string;
  onShare: () => Promise<boolean> | boolean;
  isSharing?: boolean;
  canShare?: boolean;
}

export function GameShareSheet({
  open,
  onOpenChange,
  classroomHref,
  onShare,
  isSharing = false,
  canShare = true,
}: GameShareSheetProps) {
  const nativeShare = canUseNativeShare();

  const handleShare = useCallback(async () => {
    onOpenChange(false);
    await onShare();
  }, [onOpenChange, onShare]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        overlayClassName={Z_INDEX_CLASSES.GAME_SHARE_SHEET}
        className={cn(Z_INDEX_CLASSES.GAME_SHARE_SHEET, "rounded-t-2xl")}
        hideCloseButton
        aria-describedby={undefined}
        data-testid="game-share-sheet"
      >
        <SheetTitle className="sr-only">Share this game</SheetTitle>
        <div className="flex flex-col gap-3">
          <a
            href={classroomHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="game-share-sheet-classroom"
            onClick={() => onOpenChange(false)}
            className="flex items-center gap-3 rounded-2xl bg-surface-200 p-4 text-left transition-colors hover:bg-surface-300"
          >
            <img src={CLASSROOM_ICON_SRC} alt="" className="h-10 w-10 shrink-0 object-contain" />
            <span className="min-w-0">
              <span className="block text-base font-bold text-ink">Add to Google Classroom</span>
              <span className="mt-0.5 block text-sm text-ink-700">Share with your class</span>
            </span>
          </a>
          <button
            type="button"
            data-testid="game-share-sheet-more"
            onClick={handleShare}
            disabled={isSharing || !canShare}
            className="flex items-center gap-3 rounded-2xl bg-surface-100 p-4 text-left transition-colors hover:bg-surface-200 disabled:pointer-events-none disabled:opacity-50"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-purple text-white">
              <ExportIcon weight="bold" size={22} aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-base font-bold text-ink">
                {nativeShare ? "Share…" : "Copy link"}
              </span>
              <span className="mt-0.5 block text-sm text-ink-700">
                {nativeShare ? "More ways to share" : "Copy this game page link"}
              </span>
            </span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
